import {
    Badge,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    List,
    ListItem,
    Typography,
} from '@deere/fuel-react';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox.jsx';
import styles from './WorkforceActiveJobOpenings.module.css';
import { LoggedInContext } from '../../context/LoggedInContext.jsx';
import { WorkforceContext } from '../../context/WorkforceContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import { CheckCircle } from '@mui/icons-material';

const ActiveJobRow = ({ activeJob }) => {
    const navigate = useNavigate();
    return (
        <ListItem
            onClick={() =>
                navigate(
                    '/active-job-openings/' + activeJob.jobNumber

                    //Future state - change below when employee maxed out the number of job moves allowed to make in a contract year Userstory is handled
                    /*                    ,{
                        state: {
                            variant: 'warning',
                            message:
                                'You have maxed out the number of job moves you are allowed to make in a contract year. You may bid on open jobs again when your contract year resets on ##/##/####.',
                        },
                    }*/
                )
            }
            className={styles.jobBidRow}
        >
            <Grid container className={styles.jobBidRowInner}>
                <Grid item sm={6} className={styles.jobBidRowTitle}>
                    <span>
                        {activeJob.classification} #{activeJob.jobNumber}{' '}
                    </span>
                    <span>- ({activeJob.noOfOpenings}) openings</span>
                </Grid>
                <Grid item sm={6} className={styles.jobBiddersSection}>
                    {activeJob.hasUserBid ? (
                        <span>
                            <Badge startIcon variant="success">
                                Bid Submitted
                            </Badge>
                        </span>
                    ) : activeJob.hasUserWithdrawn ? (
                        <Badge startIcon={<CheckCircle />} variant="warning">
                            Bid Withdrawn
                        </Badge>
                    ) : null}
                </Grid>
                <Grid item xs={12} className={styles.jobBidInfoSection}>
                    <span>{activeJob.facility}</span>
                    <span>Dept: {activeJob.department}</span>
                    <span>Job Grade: {activeJob.jobGrade}</span>
                    <span>Bid Close {activeJob.endDate}</span>
                    <span>Bid Withdrawal Close {activeJob.withdrawalDate}</span>
                </Grid>
            </Grid>
        </ListItem>
    );
};

export const WorkforceActiveJobOpenings = () => {
    const [activeJobOpen, setActiveJobOpen] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [alertMsg, setAlertMsg] = useState(null);
    const [alertVariant, setAlertVariant] = useState(false);
    const location = useLocation();
    const { authState, makeRequest, impersonatedId } =
        useContext(LoggedInContext);
    const { loading, activeJobBids } = useContext(WorkforceContext);

    const comparator = (a, b) => {
        const facilityA = a.facility;
        const facilityB = b.facility;
        if (facilityA < facilityB) {
            return -1;
        }
        if (facilityA > facilityB) {
            return 1;
        }
        if (facilityA === facilityB) {
            const departmentA = a.department.toUpperCase(); // ignore upper and lowercase
            const departmentB = b.department.toUpperCase(); // ignore upper and lowercase
            if (departmentA < departmentB) {
                return -1;
            }
            if (departmentA > departmentB) {
                return 1;
            }
        }
        return 0;
    };

    useEffect(() => {
        const fetchBids = async () => {
            setIsLoading(true);
            const response = await makeRequest(
                `${process.env.REACT_APP_API_ENDPOINT}/bids/active-for-user`
            );
            if (response.ok) {
                const responseData = await response.json();
                const jobBids = responseData
                    .map((job) => ({
                        jobNumber: job.id,
                        facility: job.facility,
                        department: job.department,
                        classification: job.classification.classification,
                        noOfOpenings: job.noOfOpenings,
                        jobGrade: job.jobGrade,
                        endDate: DateTime.fromISO(job.endDate).toFormat(
                            'LL/dd/yy'
                        ),
                        withdrawalDate: DateTime.fromISO(
                            job.withdrawalEndDate
                        ).toFormat('LL/dd/yy'),
                        reqNumber: job.reqNumber,
                        racfId: job.racfId,
                        status: job.status,
                        hasUserBid: activeJobBids
                            ? activeJobBids.some(
                                  (bid) =>
                                      bid.id === job.id &&
                                      bid.status === 'SUBMITTED'
                              )
                            : false,
                        hasUserWithdrawn: activeJobBids
                            ? activeJobBids.some(
                                  (bid) =>
                                      bid.id === job.id &&
                                      bid.status === 'WITHDRAWN'
                              )
                            : false,
                    }))
                    .sort(comparator);
                setActiveJobOpen(jobBids);
            } else {
                setAlertMsg(
                    errorBannerFromResponse(
                        response,
                        'We are unable to retrieve active job openings. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            }
            setIsLoading(false);
        };

        fetchBids();
    }, [authState, impersonatedId, makeRequest, activeJobBids]);

    return (
        <div className={styles.parentContainer}>
            <Box className={styles.contentToContainer}>
                {location.state?.message && (
                    <AlertBox
                        variant={location.state.variant}
                        msg={location.state.message}
                    ></AlertBox>
                )}
                {!location.state?.message && alertVariant && (
                    <AlertBox variant={alertVariant} msg={alertMsg}></AlertBox>
                )}
                <Box
                    className={styles.headerContainer}
                    display="block"
                    bgcolor="background.default"
                >
                    <Typography variant="h1" className={styles.heading}>
                        Active Job Openings
                    </Typography>
                </Box>
                {(isLoading || loading) && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!(isLoading || loading) &&
                    activeJobOpen.length === 0 &&
                    !alertVariant && (
                        <Card>
                            <CardContent>
                                <Typography gutterBottom variant="h4">
                                    There are no active jobs at this time.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                {!(isLoading || loading) && activeJobOpen.length > 0 && (
                    <>
                        <Card>
                            <List>
                                {activeJobOpen.map((activeJob, index) => (
                                    <ActiveJobRow
                                        activeJob={activeJob}
                                        key={index}
                                    />
                                ))}
                            </List>
                        </Card>
                    </>
                )}
            </Box>
        </div>
    );
};
